import { Box } from '@mui/material';
import { useThemeSwitcher } from '../../contexts/ThemeContext';

/**
 * @typedef {Object} LogoProps
 * @property {() => void} [onClick] - Optional click handler for logo interaction
 * @property {string} [className] - Additional CSS classes
 * @property {Object} [sx] - Additional Material-UI sx props
 * @property {string} [alt] - Alt text for the logo image
 * @property {'auto'|'light'|'dark'} [variant='auto'] - Logo variant to display.
 *   'auto' uses the theme's header logo (default behavior).
 *   'light' forces the logo suited for light/white backgrounds (purple/green).
 *   'dark' forces the logo suited for dark backgrounds (white/green).
 */

/**
 * EllaFin Logo component
 *
 * A reusable logo component that displays the EllaFin branding
 * with consistent styling and optional click functionality.
 * Logo path is sourced from the theme config for theme switching support.
 *
 * The `variant` prop controls which logo is shown:
 * - 'auto' (default): uses the theme's configured header logo
 * - 'light': always uses the purple/green logo for light backgrounds
 * - 'dark': always uses the white/green logo for dark backgrounds
 *
 * @param {LogoProps} props - Component props
 * @returns {JSX.Element} EllaFin logo
 */
function Logo({
  onClick,
  className,
  sx = {},
  alt = 'EllaFin\u2122 Logo',
  variant = 'auto',
}) {
  const { themeConfig } = useThemeSwitcher();

  /** Logo suited for light/white backgrounds (purple and green text) */
  const lightBgLogo = `${process.env.PUBLIC_URL}/logos/ellafin-logo.png`;
  /** Logo suited for dark backgrounds (white and green text) */
  const darkBgLogo = `${process.env.PUBLIC_URL}/images/full-logo-white-green.png`;

  /**
   * Determine which logo source to use based on the variant prop.
   * @returns {string} The logo image source URL
   */
  const getLogoSrc = () => {
    if (variant === 'light') {
      return lightBgLogo;
    }
    if (variant === 'dark') {
      return darkBgLogo;
    }
    return themeConfig.assets?.headerLogo || lightBgLogo;
  };

  return (
    <Box
      component="img"
      src={getLogoSrc()}
      alt={alt}
      className={className}
      onClick={onClick}
      sx={{
        height: '40px',
        width: 'auto',
        maxWidth: '200px',
        cursor: onClick ? 'pointer' : 'default',
        objectFit: 'contain',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              transform: 'scale(1.05)',
            }
          : {},
        ...sx,
      }}
    />
  );
}

export default Logo;
